import type { AnnualDemandUtilizationRangePct } from '../../types/siteRag';
import type { ExtractedClaim } from '../../types/siteContext';

/** Demand utilization is a positive annual operating fraction, expressed in %. */
export const ANNUAL_DEMAND_UTILIZATION_MIN_PCT = 0.1;
export const ANNUAL_DEMAND_UTILIZATION_MAX_PCT = 100;

/**
 * Validate a persisted annual data-center demand-utilization range.
 * `undefined` is valid and means the operator has not supplied a range.
 */
export function annualDemandUtilizationRangePctError(
  value: Partial<AnnualDemandUtilizationRangePct> | null | undefined,
): string | null {
  if (value == null) return null;
  if (!Number.isFinite(value.low) || !Number.isFinite(value.high)) {
    return 'Enter both the low and high annual utilization percentages.';
  }
  const low = value.low as number;
  const high = value.high as number;
  if (low < ANNUAL_DEMAND_UTILIZATION_MIN_PCT || high < ANNUAL_DEMAND_UTILIZATION_MIN_PCT) {
    return `Annual utilization must be at least ${ANNUAL_DEMAND_UTILIZATION_MIN_PCT}%.`;
  }
  if (low > ANNUAL_DEMAND_UTILIZATION_MAX_PCT || high > ANNUAL_DEMAND_UTILIZATION_MAX_PCT) {
    return 'Annual utilization cannot exceed 100%.';
  }
  if (high < low) return 'The high annual utilization must be at least the low value.';
  return null;
}

export function isAnnualDemandUtilizationRangePct(
  value: unknown,
): value is AnnualDemandUtilizationRangePct {
  if (value == null || typeof value !== 'object') return false;
  return annualDemandUtilizationRangePctError(
    value as Partial<AnnualDemandUtilizationRangePct>,
  ) == null;
}

export interface ContextDemandUtilizationResolution {
  range: AnnualDemandUtilizationRangePct;
  claimId: string;
  sourceLabel: string;
}

function loadFactorPctFromClaim(claim: ExtractedClaim): number | null {
  if (
    claim.state === 'mis-extracted'
    || claim.category !== 'power.grid'
    || claim.source !== 'document-extracted'
    || !claim.sourceDocId?.trim()
  ) return null;
  const sourceText = claim.sourceSpan.replaceAll('\n', ' ');
  const claimText = claim.claim.replaceAll('\n', ' ');
  const text = `${sourceText} ${claimText}`;
  if (!/\b(?:load[ -]?factor|demand utili[sz]ation)\b/i.test(text)) return null;
  // Capacity factor belongs to generation and power factor belongs to the
  // electrical stack. A mixed sentence is ambiguous, so do not auto-promote.
  if (/\b(?:capacity factor|power factor|generator|generation|turbine|reactor|solar|wind)\b/i.test(text)) return null;
  const labeledPct = (candidate: string): number | null => {
    const afterLabel = candidate.match(/\b(?:load[ -]?factor|demand utili[sz]ation)\b[^\d]{0,32}(\d+(?:\.\d+)?)\s*(?:%|percent\b)/i);
    const beforeLabel = candidate.match(/(\d+(?:\.\d+)?)\s*(?:%|percent)\s+(?:annual\s+)?(?:data[- ]center\s+)?(?:load[ -]?factor|demand utili[sz]ation)\b/i);
    const value = Number(afterLabel?.[1] ?? beforeLabel?.[1]);
    return Number.isFinite(value) ? value : null;
  };
  const parsed = labeledPct(sourceText) ?? labeledPct(claimText);
  if (parsed == null || !Number.isFinite(parsed) || parsed < ANNUAL_DEMAND_UTILIZATION_MIN_PCT || parsed > 100) return null;
  const claimed = typeof claim.claimedValue === 'number'
    ? claim.claimedValue
    : typeof claim.claimedValue === 'string' && claim.claimedValue.trim() !== ''
      ? Number(claim.claimedValue)
      : Number.NaN;
  if (!Number.isFinite(claimed) || Math.abs(claimed - parsed) > 0.05) return null;
  const sourceNumbers = [...sourceText.matchAll(/-?\d+(?:\.\d+)?/g)].map(match => Number(match[0]));
  if (!sourceNumbers.some(value => Number.isFinite(value) && Math.abs(value - claimed) <= 0.05)) return null;
  return parsed;
}

/**
 * Recover an explicit data-center load-factor claim from trusted Site Context.
 * Conflicting values fail closed; generator capacity factors are excluded.
 */
export function demandUtilizationFromContextClaims(
  claims: readonly ExtractedClaim[] | null | undefined,
): ContextDemandUtilizationResolution | null {
  const matches = (claims ?? []).flatMap(claim => {
    const pct = loadFactorPctFromClaim(claim);
    return pct == null ? [] : [{ claim, pct }];
  });
  if (matches.length === 0) return null;
  const distinct = [...new Set(matches.map(match => match.pct))];
  if (distinct.length !== 1) return null;
  const selected = matches[0]!;
  const supportingDocumentCount = new Set(matches.map(match => match.claim.sourceDocId)).size;
  return {
    range: { low: selected.pct, high: selected.pct },
    claimId: selected.claim.id,
    sourceLabel: `Document-backed Site Context · declared data-center load factor (${selected.pct}%)${supportingDocumentCount > 1 ? ` · ${supportingDocumentCount} agreeing documents` : ''}`,
  };
}
